/**
 * LeafletComparisonMap - Comparison Map Component
 * Thay thế GoogleMapComponent
 *
 * Features:
 * - Center project marker (đỏ) với pulse animation
 * - Nearby project markers (màu theo segment)
 * - Radius circle (km)
 * - Popup info window
 * - Auto fit bounds
 */
import { useEffect, useRef, useState, useMemo } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import clsx from 'clsx'
import { TILE_LAYERS } from './LeafletMap'

// Default center: Da Nang
const DEFAULT_CENTER = [16.0544, 108.2022]
const DEFAULT_ZOOM = 13

// Marker colors by segment
const MARKER_COLORS = {
  center: '#ef4444', // red
  cao_cap: '#8b5cf6', // purple
  trung_cap: '#3b82f6', // blue
  binh_dan: '#22c55e', // green
  nghi_duong: '#f59e0b', // amber
}

// Segment labels
const SEGMENT_LABELS = {
  cao_cap: 'Cao cấp',
  trung_cap: 'Trung cấp',
  binh_dan: 'Bình dân',
  nghi_duong: 'Nghỉ dưỡng',
}

// Create circle marker icon
function createCircleIcon(color, size = 24, isCenter = false) {
  const borderWidth = isCenter ? 4 : 3
  const innerSize = size - borderWidth * 2

  return L.divIcon({
    className: 'circle-marker-wrapper',
    html: `
      <div class="circle-marker" style="width: ${size}px; height: ${size}px;">
        ${isCenter ? '<div class="hero-pulse"></div>' : ''}
        <div class="circle-marker-dot" style="
          width: ${size}px;
          height: ${size}px;
          background: ${color};
          border: ${borderWidth}px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        "></div>
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  })
}

// Format price
function formatPrice(price) {
  if (!price) return '-'
  if (price >= 1000000000) {
    return `${(price / 1000000000).toFixed(1)} tỷ`
  }
  return `${(price / 1000000).toFixed(0)} tr`
}

// Create popup content
function createPopupContent(project, isCenter = false, onDetailClick) {
  const segment = project.segment || 'trung_cap'
  const segmentLabel = SEGMENT_LABELS[segment] || segment
  const segmentColor = MARKER_COLORS[segment] || MARKER_COLORS.trung_cap

  const badgeColors = {
    cao_cap: 'background: #f3e8ff; color: #7c3aed;',
    trung_cap: 'background: #dbeafe; color: #2563eb;',
    binh_dan: 'background: #dcfce7; color: #16a34a;',
    nghi_duong: 'background: #fef3c7; color: #d97706;',
  }

  return `
    <div class="map-popup" style="min-width: 220px;">
      <div style="padding: 12px;">
        <div style="display: flex; gap: 6px; margin-bottom: 8px; flex-wrap: wrap;">
          ${isCenter ? '<span style="display: inline-flex; padding: 2px 8px; border-radius: 12px; font-size: 10px; font-weight: 600; background: #fee2e2; color: #dc2626;">TRUNG TÂM</span>' : ''}
          <span style="display: inline-flex; padding: 2px 8px; border-radius: 12px; font-size: 10px; font-weight: 600; ${badgeColors[segment] || badgeColors.trung_cap}">
            ${segmentLabel}
          </span>
        </div>

        <h3 style="font-weight: 600; font-size: 14px; color: #111827; margin-bottom: 4px;">
          ${project.name || 'Không có tên'}
        </h3>

        <p style="font-size: 12px; color: #6B7280; margin-bottom: 8px;">
          ${project.address || project.location || ''}
        </p>

        <div style="display: flex; flex-direction: column; gap: 4px; font-size: 12px;">
          <div style="display: flex; justify-content: space-between;">
            <span style="color: #6B7280;">Giá:</span>
            <span style="font-weight: 500; color: #111827;">
              ${formatPrice(project.price_from)} - ${formatPrice(project.price_to)}
            </span>
          </div>
          ${project.price_per_m2 ? `
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #6B7280;">Giá/m²:</span>
              <span style="font-weight: 500; color: #2563eb;">
                ${(project.price_per_m2 / 1000000).toFixed(0)} tr/m²
              </span>
            </div>
          ` : ''}
          ${project.distance_km !== undefined ? `
            <div style="display: flex; justify-content: space-between;">
              <span style="color: #6B7280;">Khoảng cách:</span>
              <span style="font-weight: 500; color: #111827;">
                ${project.distance_km} km
              </span>
            </div>
          ` : ''}
        </div>

        <button
          class="leaflet-popup-detail-btn"
          data-project-id="${project.id}"
          style="
            margin-top: 12px;
            width: 100%;
            padding: 8px 16px;
            font-size: 12px;
            font-weight: 500;
            color: white;
            background: #2563eb;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            transition: background 0.2s;
          "
          onmouseover="this.style.background='#1d4ed8'"
          onmouseout="this.style.background='#2563eb'"
        >
          Xem chi tiết
        </button>
      </div>
    </div>
  `
}

function LeafletComparisonMap({
  centerProject,
  nearbyProjects = [],
  radiusKm = 5,
  onProjectClick,
  tileLayer = 'cartodb_light',
}) {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const markersRef = useRef([])
  const circleRef = useRef(null)
  const [isReady, setIsReady] = useState(false)

  // Calculate center
  const center = useMemo(() => {
    if (centerProject?.latitude && centerProject?.longitude) {
      return [parseFloat(centerProject.latitude), parseFloat(centerProject.longitude)]
    }
    return DEFAULT_CENTER
  }, [centerProject])

  // Initialize map
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    // Create map
    const map = L.map(containerRef.current, {
      center: center,
      zoom: DEFAULT_ZOOM,
      zoomControl: true,
      attributionControl: true,
    })

    mapRef.current = map

    // Add tile layer
    const tileConfig = TILE_LAYERS[tileLayer] || TILE_LAYERS.cartodb_light
    L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19,
    }).addTo(map)

    // Handle popup button clicks
    map.on('popupopen', (e) => {
      const btn = e.popup._contentNode?.querySelector('.leaflet-popup-detail-btn')
      if (btn && onProjectClick) {
        btn.onclick = () => {
          const projectId = btn.dataset.projectId
          const allProjects = [centerProject, ...nearbyProjects].filter(Boolean)
          const project = allProjects.find((p) => String(p.id) === projectId)
          if (project) {
            onProjectClick(project)
          }
        }
      }
    })

    setIsReady(true)

    // Cleanup
    return () => {
      if (mapRef.current) {
        mapRef.current.remove()
        mapRef.current = null
      }
    }
  }, [])

  // Update markers and circle when data changes
  useEffect(() => {
    if (!mapRef.current || !isReady) return

    const map = mapRef.current

    // Clear existing markers
    markersRef.current.forEach((m) => map.removeLayer(m))
    markersRef.current = []

    // Clear existing circle
    if (circleRef.current) {
      map.removeLayer(circleRef.current)
      circleRef.current = null
    }

    // Add radius circle
    if (centerProject?.latitude && centerProject?.longitude) {
      const circleCenter = [
        parseFloat(centerProject.latitude),
        parseFloat(centerProject.longitude),
      ]

      circleRef.current = L.circle(circleCenter, {
        radius: radiusKm * 1000,
        color: '#3b82f6',
        weight: 2,
        opacity: 0.5,
        fillColor: '#3b82f6',
        fillOpacity: 0.1,
      }).addTo(map)
    }

    // Collect bounds
    const bounds = L.latLngBounds([])

    // Add center project marker
    if (centerProject?.latitude && centerProject?.longitude) {
      const lat = parseFloat(centerProject.latitude)
      const lng = parseFloat(centerProject.longitude)

      const marker = L.marker([lat, lng], {
        icon: createCircleIcon(MARKER_COLORS.center, 28, true),
        zIndexOffset: 1000,
      }).addTo(map)

      marker.bindPopup(createPopupContent(centerProject, true, onProjectClick), {
        maxWidth: 280,
        className: 'custom-popup',
      })

      markersRef.current.push(marker)
      bounds.extend([lat, lng])
    }

    // Add nearby project markers
    nearbyProjects.forEach((project) => {
      if (!project.latitude || !project.longitude) return

      const lat = parseFloat(project.latitude)
      const lng = parseFloat(project.longitude)
      const color = MARKER_COLORS[project.segment] || MARKER_COLORS.trung_cap

      const marker = L.marker([lat, lng], {
        icon: createCircleIcon(color, 22, false),
      }).addTo(map)

      marker.bindPopup(createPopupContent(project, false, onProjectClick), {
        maxWidth: 280,
        className: 'custom-popup',
      })

      markersRef.current.push(marker)
      bounds.extend([lat, lng])
    })

    // Fit bounds if we have markers
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 })
    }
  }, [centerProject, nearbyProjects, radiusKm, isReady, onProjectClick])

  // Update tile layer
  useEffect(() => {
    if (!mapRef.current || !isReady) return

    const map = mapRef.current
    const tileConfig = TILE_LAYERS[tileLayer] || TILE_LAYERS.cartodb_light

    // Remove existing tile layers
    map.eachLayer((layer) => {
      if (layer instanceof L.TileLayer) {
        map.removeLayer(layer)
      }
    })

    // Add new tile layer
    L.tileLayer(tileConfig.url, {
      attribution: tileConfig.attribution,
      maxZoom: 19,
    }).addTo(map)
  }, [tileLayer, isReady])

  return (
    <div className="w-full h-full relative">
      <div
        ref={containerRef}
        className="w-full h-full"
        style={{ minHeight: '300px', background: '#E8E0D5' }}
      />

      {/* Loading overlay */}
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Đang tải bản đồ...</p>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-3 z-[500]">
        <div className="text-xs font-medium text-gray-700 mb-2">Chú thích</div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full border-2 border-white shadow"
              style={{ background: MARKER_COLORS.center }}
            />
            <span className="text-xs text-gray-600">Dự án trung tâm</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full border-2 border-white shadow"
              style={{ background: MARKER_COLORS.cao_cap }}
            />
            <span className="text-xs text-gray-600">Cao cấp</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full border-2 border-white shadow"
              style={{ background: MARKER_COLORS.trung_cap }}
            />
            <span className="text-xs text-gray-600">Trung cấp</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full border-2 border-white shadow"
              style={{ background: MARKER_COLORS.binh_dan }}
            />
            <span className="text-xs text-gray-600">Bình dân</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full border-2 border-white shadow"
              style={{ background: MARKER_COLORS.nghi_duong }}
            />
            <span className="text-xs text-gray-600">Nghỉ dưỡng</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LeafletComparisonMap
